/**
 * EdgeDetailsPanel - Panel de detalles de edge/segmento en el RightPanel
 *
 * @version 2.0
 * @created 2026-03-03
 * @updated 2026-04-11 - Consolidación del modo análisis con Prioridad Estructural
 * @module core/layouts/panels/RightPanel
 *
 * @description
 * Panel con tabs para visualizar y editar propiedades del edge seleccionado.
 *
 * Tabs:
 * - General: Info básica (ID, conexión, geometría, origen, metadata)
 * - Caracterización: 7 secciones editables (functionalClass, streetForm, etc.)
 * - Sintaxis Espacial: Métricas reales del segmento seleccionado
 * - Prioridad Estructural: Síntesis del rol estratégico del segmento
 *
 * @remarks
 * Modelo multinivel: separación entre caracterización manual y métricas derivadas
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, GitBranch, MapPin, Layers, Info, Navigation, Gauge, Save, FileText, Activity, Route, Building2, Bike, Construction, Zap, ShieldAlert, TrendingUp, Braces, Users, ChevronDown } from 'lucide-react';
import { canonicalNetworkApi } from '../../../spatial/services/canonical-network-api.service';
import { SuccessToast, SaveConfirmDialog } from '../../../components';
import type {
  CanonicalEdge,
  AccessMode,
  OnewayDirection,
  SurfaceType,
  StreetForm,
  ExclusiveMode,
  AccessRestriction,
  ParkingConfig,
  ConditionType,
  SmoothnessType,
} from '../../../spatial/types';
import { useSyntaxAnalysis, type SyntaxMetricType } from '../../../spatial/contexts/SyntaxAnalysisContext';
import { usePopulationSegmentSafe, type PopulationVariable } from '../../../spatial/contexts/PopulationSegmentContext';
import { COLOR_RAMPS, DEFAULT_SYNTAX_RAMP } from '../../../spatial/config/syntax-color-ramps';
import { populationSegmentApi, type PopulationSegmentDetail } from '../../../spatial/services/population-segment-api.service';
import {
  ANALYSIS_HELP_TEXT,
  DERIVED_ANALYSIS_LABEL,
  HIERARCHY_PROFILE_DESCRIPTIONS,
  HIERARCHY_PROFILE_LABELS,
  STRUCTURE_BAND_LABELS,
  SYNTAX_METRIC_DESCRIPTIONS,
  SYNTAX_METRIC_LABELS,
  useNetworkAnalysisUiStore,
} from '../../../spatial';
import type { SyntaxMetric } from '../../../spatial/services/syntax-api.service';
import { useRightPanel } from './RightPanelContext';
import { AssociatedObjectsSection } from '../../../spatial/components/AssociatedObjectsSection';
import type { HierarchyFeatureProperties, HierarchyProfile } from '../../../spatial/services/network-hierarchy-api.service';
import type { EdgeSemanticInfo } from '../../../spatial/types';
import {
  formatConfidenceScore,
  getEdgeNamingStatusLabel,
  getEdgeNamingStatusTone,
  getEdgeSemanticDisplayName,
  getResolvedCrossStreet,
  getRoadTypeLabel,
} from '../../../spatial/utils/edge-semantics-ui';
import './edge-details-panel.css';

/**
 * Tipo de tab activo
 */
export type EdgeDetailTab = 'general' | 'caracterizacion' | 'syntax' | 'structure' | 'population';

function formatAnalysisValue(value: number | null | undefined, decimals = 3): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/D';
  }

  if (value === 0) {
    return '0';
  }

  if (Math.abs(value) < 0.001) {
    return value.toExponential(2);
  }

  return value.toFixed(decimals);
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/D';
  }

  return `${(value * 100).toFixed(1)}%`;
}

function formatLengthMeters(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/D';
  }

  return `${value.toFixed(2)} m`;
}

function formatDateTime(value: Date | null): string {
  if (!value) {
    return 'N/D';
  }

  return `${value.toLocaleDateString('es-AR')} ${value.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function getSelectedSyntaxMetricValue(
  syntaxMetric: SyntaxMetric | null,
  selectedMetric: SyntaxMetricType,
): number | null {
  if (!syntaxMetric) {
    return null;
  }

  switch (selectedMetric) {
    case 'integration_global':
      return syntaxMetric.integrationGlobal;
    case 'integration_global_raw':
      return syntaxMetric.integrationGlobalRaw;
    case 'integration_r1200':
      return syntaxMetric.integrationR1200;
    case 'integration_r800':
      return syntaxMetric.integrationR800;
    case 'choice_global':
      return syntaxMetric.choiceGlobal;
    case 'choice_r1200':
      return syntaxMetric.choiceR1200;
    case 'choice_r800':
      return syntaxMetric.choiceR800;
    default:
      return null;
  }
}

function getHierarchyBandLabel(band: HierarchyFeatureProperties['hierarchyBand']): string {
  return STRUCTURE_BAND_LABELS[band];
}

/**
 * Props del componente EdgeDetailsPanel
 */
export interface EdgeDetailsPanelProps {
  /**
   * ID del edge a mostrar
   */
  edgeId: string;

  /**
   * ID del proyecto activo (necesario para asociaciones)
   */
  projectId?: string | null;

  /**
   * Datos del análisis derivado del edge (opcional)
   * Se muestra en el tab de Prioridad Estructural si está presente
   */
  hierarchyData?: HierarchyFeatureProperties | null;

  /**
   * Perfil activo del análisis de jerarquía
   */
  hierarchyProfile?: HierarchyProfile;

  /**
   * Tab inicial contextual del inspector
   */
  initialTab?: EdgeDetailTab;

  /**
   * Callback cuando el usuario cierra el panel
   */
  onClose: () => void;
}

/**
 * Panel de detalles de edge en el RightPanel.
 *
 * @remarks
 * Proporciona información completa sobre el edge seleccionado.
 */
export function EdgeDetailsPanel({
  edgeId,
  projectId,
  hierarchyData,
  hierarchyProfile = 'equilibrado',
  initialTab = 'general',
  onClose,
}: EdgeDetailsPanelProps): JSX.Element {
  const { registerUnsavedChangesCheck } = useRightPanel();
  const {
    status: syntaxStatus,
    metrics: syntaxMetrics,
    calculatedAt: syntaxCalculatedAt,
    selectedMetric,
    currentMetricVisualRange,
    syntaxVersion,
    syntaxBasedOnNetworkVersion,
    urbanNetworkVersion,
    isStale,
  } = useSyntaxAnalysis();
  const {
    analysisActive,
    setAnalysisActive,
    setActiveIndicator,
  } = useNetworkAnalysisUiStore();
  const [edge, setEdge] = useState<CanonicalEdge | null>(null);
  const [semantic, setSemantic] = useState<EdgeSemanticInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [semanticError, setSemanticError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Sistema de tabs
  const [activeTab, setActiveTab] = useState<EdgeDetailTab>(initialTab);
  const previousEdgeIdRef = useRef(edgeId);
  const previousInitialTabRef = useRef(initialTab);

  // Estados editables para todos los campos de caracterización
  const [editedRoadClass, setEditedRoadClass] = useState<string | null>(null);
  const [editedAccessMode, setEditedAccessMode] = useState<AccessMode[]>([]);
  const [editedOneway, setEditedOneway] = useState<boolean>(false);
  const [editedOnewayDirection, setEditedOnewayDirection] = useState<OnewayDirection | null>(null);
  const [editedLanes, setEditedLanes] = useState<number | null>(null);
  const [editedSurfaceType, setEditedSurfaceType] = useState<SurfaceType | null>(null);
  const [editedSpeedLimit, setEditedSpeedLimit] = useState<number | null>(null);
  const [editedTypicalSpeed, setEditedTypicalSpeed] = useState<number | null>(null);
  // Nuevos campos multinivel (Plan Red Urbana UX)
  const [editedStreetForm, setEditedStreetForm] = useState<StreetForm | null>(null);
  const [editedExclusiveMode, setEditedExclusiveMode] = useState<ExclusiveMode | null>(null);
  const [editedAccessRestriction, setEditedAccessRestriction] = useState<AccessRestriction | null>(null);
  const [editedParking, setEditedParking] = useState<ParkingConfig | null>(null);
  const [editedCondition, setEditedCondition] = useState<ConditionType | null>(null);
  const [editedSmoothness, setEditedSmoothness] = useState<SmoothnessType | null>(null);
  const [editedMaxHeightM, setEditedMaxHeightM] = useState<number | null>(null);
  const [editedMaxWeightT, setEditedMaxWeightT] = useState<number | null>(null);
  const [editedMaxWidthM, setEditedMaxWidthM] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ── Población por segmento ────────────────────────────────────────────────
  const populationCtx = usePopulationSegmentSafe();
  const populationModeActive = populationCtx?.populationModeActive ?? false;
  const populationEdgeProps  = populationCtx?.edgeData.get(edgeId) ?? null;
  const populationRanges     = populationCtx?.ranges ?? null;
  const selectedPopVariable  = populationCtx?.selectedVariable ?? 'population_per_km';
  const populationRunStatus  = populationCtx?.runStatus ?? 'no_runs';
  const selectedPopMethod    = populationCtx?.selectedMethod ?? 'census_network_length';

  const [populationDetail, setPopulationDetail] = useState<PopulationSegmentDetail | null>(null);
  const [populationDetailLoading, setPopulationDetailLoading] = useState(false);
  const [populationDetailError, setPopulationDetailError] = useState<string | null>(null);
  const [contributionsExpanded, setContributionsExpanded] = useState(false);

  // Cargar detalle cuando el tab de población se activa (o cambia el método)
  useEffect(() => {
    if (activeTab !== 'population' || !projectId || populationRunStatus !== 'completed') {
      return;
    }
    let cancelled = false;
    setPopulationDetailLoading(true);
    setPopulationDetailError(null);
    setPopulationDetail(null);
    populationSegmentApi.getDetail(projectId, edgeId, selectedPopMethod)
      .then((detail) => { if (!cancelled) { setPopulationDetail(detail); setContributionsExpanded(false); } })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          setPopulationDetailError('Sesión expirada. Recargá la página.');
        } else if (status === 404) {
          setPopulationDetailError(null); // sin run — se maneja via runStatus
        } else {
          setPopulationDetailError('Error al cargar el detalle poblacional.');
        }
      })
      .finally(() => { if (!cancelled) setPopulationDetailLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, edgeId, projectId, populationRunStatus, selectedPopMethod]);

  // Limpiar detalle al cambiar de edge
  useEffect(() => {
    setPopulationDetail(null);
    setPopulationDetailError(null);
  }, [edgeId]);

  /** Computes the display value for a PopulationVariable given edge properties. */
  const getPopValue = useCallback((variable: PopulationVariable): number | null => {
    if (!populationEdgeProps) return null;
    const props = populationEdgeProps as unknown as Record<string, number | null | undefined>;
    // network_length_m es null para building_refined → fallback a length_meters (de canonical_edges)
    const rawLen = props.network_length_m ?? props.length_meters;
    const km = rawLen != null && rawLen > 0 ? rawLen / 1000 : null;
    switch (variable) {
      case 'population_per_km':   return props.population_estimated != null && km != null ? props.population_estimated / km : null;
      case 'population':          return props.population_estimated ?? null;
      case 'households_per_km':   return props.households_estimated != null && km != null ? props.households_estimated / km : null;
      case 'households':          return props.households_estimated ?? null;
      case 'nbi_households_per_km': return props.nbi_households_estimated != null && km != null ? props.nbi_households_estimated / km : null;
      case 'nbi_households':      return props.nbi_households_estimated ?? null;
    }
  }, [populationEdgeProps]);

  /** Percentile rank [0,1] of value within [min, p95] for selected variable. */
  const populationPercentileRank = useMemo(() => {
    if (!populationRanges) return null;
    const range = populationRanges[selectedPopVariable];
    if (!range) return null;
    const val = getPopValue(selectedPopVariable);
    if (val === null) return null;
    const span = range.p95 - range.min || 1;
    return Math.min(Math.max((val - range.min) / span, 0), 1);
  }, [getPopValue, populationRanges, selectedPopVariable]);

  const syntaxMetric = useMemo(() => {
    return syntaxMetrics.find((metric) => metric.edgeId === edgeId) ?? null;
  }, [edgeId, syntaxMetrics]);

  const selectedSyntaxMetricValue = useMemo(() => {
    return getSelectedSyntaxMetricValue(syntaxMetric, selectedMetric);
  }, [selectedMetric, syntaxMetric]);

  const semanticFromCrossStreet = useMemo(
    () => getResolvedCrossStreet(semantic?.fromStreetCandidates),
    [semantic],
  );

  const semanticToCrossStreet = useMemo(
    () => getResolvedCrossStreet(semantic?.toStreetCandidates),
    [semantic],
  );

  const panelTitle = useMemo(() => {
    if (loading || (semanticLoading && !semantic)) {
      return 'Cargando identificación...';
    }

    return getEdgeSemanticDisplayName(semantic);
  }, [loading, semantic, semanticLoading]);

  const hasSyntaxMetrics = useMemo(() => {
    if (!syntaxMetric) {
      return false;
    }

    return [
      syntaxMetric.integrationGlobal,
      syntaxMetric.integrationGlobalRaw,
      syntaxMetric.integrationR1200,
      syntaxMetric.integrationR800,
      syntaxMetric.choiceGlobal,
      syntaxMetric.choiceR1200,
      syntaxMetric.choiceR800,
    ].some((value) => value !== null && value !== undefined);
  }, [syntaxMetric]);

  useEffect(() => {
    if (previousEdgeIdRef.current !== edgeId) {
      setActiveTab(initialTab);
      setSuccessMessage(null);
      previousEdgeIdRef.current = edgeId;
      previousInitialTabRef.current = initialTab;
      return;
    }

    if (previousInitialTabRef.current === initialTab) {
      return;
    }

    previousInitialTabRef.current = initialTab;

    const canFollowContext =
      activeTab === 'general' ||
      activeTab === 'syntax' ||
      activeTab === 'structure' ||
      activeTab === 'population';

    if (!canFollowContext) {
      return;
    }

    const nextTab =
      initialTab === 'population' && analysisActive
        ? 'population'
        : initialTab === 'syntax' && analysisActive
          ? 'syntax'
          : initialTab === 'structure' && analysisActive
            ? 'structure'
            : !analysisActive
              ? 'general'
              : initialTab;

    setActiveTab(nextTab);
  }, [activeTab, analysisActive, edgeId, initialTab]);

  useEffect(() => {
    if (activeTab === 'syntax') {
      setAnalysisActive(true);
      setActiveIndicator(selectedMetric);
    } else if (activeTab === 'structure') {
      setAnalysisActive(true);
      setActiveIndicator('structure');
    } else if (activeTab === 'population') {
      setAnalysisActive(true);
      setActiveIndicator('population');
    }
  }, [activeTab, selectedMetric, setActiveIndicator, setAnalysisActive]);

  /**
   * Carga los detalles del edge
   */
  useEffect(() => {
    const loadEdge = async () => {
      if (!projectId) {
        setError('No hay proyecto activo');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setSemanticLoading(true);
      setSemanticError(null);
      try {
        const [edgeData, semanticData] = await Promise.all([
          canonicalNetworkApi.getEdge(edgeId),
          canonicalNetworkApi.getEdgeSemanticName(projectId, edgeId)
            .catch((err: unknown): EdgeSemanticInfo | null => {
              const status = (err as { response?: { status?: number } })?.response?.status;
              if (status === 404) {
                return null;
              }

              console.error('[EdgeDetailsPanel] Error loading edge semantics:', err);
              setSemanticError('No se pudo cargar la identificación semántica.');
              return null;
            }),
        ]);
        setEdge(edgeData);
        setSemantic(semanticData);

        // Inicializar todos los estados editables
        setEditedRoadClass(edgeData.roadClass);
        setEditedAccessMode(edgeData.accessMode || []);
        setEditedOneway(edgeData.oneway);
        setEditedOnewayDirection(edgeData.onewayDirection);
        setEditedLanes(edgeData.lanes);
        setEditedSurfaceType(edgeData.surfaceType);
        setEditedSpeedLimit(edgeData.speedLimitKmh);
        setEditedTypicalSpeed(edgeData.typicalSpeedKmh);
        // Nuevos campos multinivel
        setEditedStreetForm(edgeData.streetForm);
        setEditedExclusiveMode(edgeData.exclusiveMode);
        setEditedAccessRestriction(edgeData.accessRestriction);
        setEditedParking(edgeData.parking);
        setEditedCondition(edgeData.condition);
        setEditedSmoothness(edgeData.smoothness);
        setEditedMaxHeightM(edgeData.maxHeightM);
        setEditedMaxWeightT(edgeData.maxWeightT);
        setEditedMaxWidthM(edgeData.maxWidthM);

        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('[EdgeDetailsPanel] Error loading edge:', err);
        setError('Error al cargar detalles del segmento');
      } finally {
        setLoading(false);
        setSemanticLoading(false);
      }
    };

    void loadEdge();
  }, [edgeId, projectId]);

  useEffect(() => {
    setSemantic(null);
    setSemanticError(null);
  }, [edgeId]);

  /**
   * Detectar cambios en cualquier campo editable
   */
  useEffect(() => {
    if (!edge) {
      setHasUnsavedChanges(false);
      return;
    }

    const hasChanges =
      editedRoadClass !== edge.roadClass ||
      JSON.stringify(editedAccessMode) !== JSON.stringify(edge.accessMode || []) ||
      editedOneway !== edge.oneway ||
      editedOnewayDirection !== edge.onewayDirection ||
      editedLanes !== edge.lanes ||
      editedSurfaceType !== edge.surfaceType ||
      editedSpeedLimit !== edge.speedLimitKmh ||
      editedTypicalSpeed !== edge.typicalSpeedKmh ||
      // Nuevos campos multinivel
      editedStreetForm !== edge.streetForm ||
      editedExclusiveMode !== edge.exclusiveMode ||
      editedAccessRestriction !== edge.accessRestriction ||
      editedParking !== edge.parking ||
      editedCondition !== edge.condition ||
      editedSmoothness !== edge.smoothness ||
      editedMaxHeightM !== edge.maxHeightM ||
      editedMaxWeightT !== edge.maxWeightT ||
      editedMaxWidthM !== edge.maxWidthM;

    setHasUnsavedChanges(hasChanges);
  }, [
    edge,
    editedRoadClass,
    editedAccessMode,
    editedOneway,
    editedOnewayDirection,
    editedLanes,
    editedSurfaceType,
    editedSpeedLimit,
    editedTypicalSpeed,
    editedStreetForm,
    editedExclusiveMode,
    editedAccessRestriction,
    editedParking,
    editedCondition,
    editedSmoothness,
    editedMaxHeightM,
    editedMaxWeightT,
    editedMaxWidthM,
  ]);

  /**
   * Registrar función de verificación de cambios sin guardar
   */
  useEffect(() => {
    // Función que retorna true si NO hay cambios (puede cerrar)
    // o false si hay cambios y usuario cancela
    const checkFn = async (): Promise<boolean> => {
      if (!hasUnsavedChanges) return true; // No hay cambios, puede cerrar

      const confirmDiscard = window.confirm(
        '¿Descartar cambios sin guardar?\n\nTienes cambios sin guardar en la caracterización del segmento. ¿Deseas descartarlos?'
      );
      return confirmDiscard;
    };

    registerUnsavedChangesCheck(checkFn);

    return () => {
      // Desregistrar pasando null
      registerUnsavedChangesCheck(null);
    };
  }, [hasUnsavedChanges, registerUnsavedChangesCheck]);

  /**
   * Guardar cambios al backend
   */
  const handleSave = useCallback(async () => {
    if (!edge || !hasUnsavedChanges) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      // Construir objeto con solo los campos que cambiaron
      const updates: any = {};

      if (editedRoadClass !== edge.roadClass) {
        updates.roadClass = editedRoadClass;
      }
      if (JSON.stringify(editedAccessMode) !== JSON.stringify(edge.accessMode || [])) {
        updates.accessMode = editedAccessMode.length > 0 ? editedAccessMode : null;
      }
      if (editedOneway !== edge.oneway) {
        updates.oneway = editedOneway;
      }
      if (editedOnewayDirection !== edge.onewayDirection) {
        updates.onewayDirection = editedOnewayDirection;
      }
      if (editedLanes !== edge.lanes) {
        updates.lanes = editedLanes;
      }
      if (editedSurfaceType !== edge.surfaceType) {
        updates.surfaceType = editedSurfaceType;
      }
      if (editedSpeedLimit !== edge.speedLimitKmh) {
        updates.speedLimitKmh = editedSpeedLimit;
      }
      if (editedTypicalSpeed !== edge.typicalSpeedKmh) {
        updates.typicalSpeedKmh = editedTypicalSpeed;
      }
      // Nuevos campos multinivel
      if (editedStreetForm !== edge.streetForm) {
        updates.streetForm = editedStreetForm;
      }
      if (editedExclusiveMode !== edge.exclusiveMode) {
        updates.exclusiveMode = editedExclusiveMode;
      }
      if (editedAccessRestriction !== edge.accessRestriction) {
        updates.accessRestriction = editedAccessRestriction;
      }
      if (editedParking !== edge.parking) {
        updates.parking = editedParking;
      }
      if (editedCondition !== edge.condition) {
        updates.condition = editedCondition;
      }
      if (editedSmoothness !== edge.smoothness) {
        updates.smoothness = editedSmoothness;
      }
      if (editedMaxHeightM !== edge.maxHeightM) {
        updates.maxHeightM = editedMaxHeightM;
      }
      if (editedMaxWeightT !== edge.maxWeightT) {
        updates.maxWeightT = editedMaxWeightT;
      }
      if (editedMaxWidthM !== edge.maxWidthM) {
        updates.maxWidthM = editedMaxWidthM;
      }

      if (!projectId) {
        throw new Error('No hay proyecto activo');
      }
      await canonicalNetworkApi.updateEdge(edgeId, updates);

      // Actualizar estado local con los nuevos valores
      setEdge({
        ...edge,
        ...updates,
      });
      setHasUnsavedChanges(false);

      // Emitir evento para que el mapa se actualice
      window.dispatchEvent(new CustomEvent('network-edge-updated', {
        detail: { edgeId },
      }));

      // Mostrar toast de éxito
      setSuccessMessage('Segmento actualizado correctamente');
      console.log('[EdgeDetailsPanel] ✓ Edge actualizado exitosamente');
    } catch (err) {
      console.error('[EdgeDetailsPanel] Error guardando cambios:', err);
      setError('Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  }, [
    edge,
    edgeId,
    hasUnsavedChanges,
    editedRoadClass,
    editedAccessMode,
    editedOneway,
    editedOnewayDirection,
    editedLanes,
    editedSurfaceType,
    editedSpeedLimit,
    editedTypicalSpeed,
    editedStreetForm,
    editedExclusiveMode,
    editedAccessRestriction,
    editedParking,
    editedCondition,
    editedSmoothness,
    editedMaxHeightM,
    editedMaxWeightT,
    editedMaxWidthM,
  ]);

  /**
   * Manejador de cambio de tab con validación de cambios pendientes
   */
  const handleTabChange = useCallback(async (newTab: EdgeDetailTab) => {
    // Si no hay cambios o es el mismo tab, cambiar directamente
    if (!hasUnsavedChanges || newTab === activeTab) {
      setActiveTab(newTab);
      return;
    }

    // Hay cambios pendientes - mostrar diálogo de confirmación
    const result = await SaveConfirmDialog.show({
      title: '¿Descartar cambios?',
      message: 'Tienes cambios sin guardar en la caracterización del segmento.',
      discardText: 'Descartar',
      cancelText: 'Cancelar',
      saveText: 'Guardar',
    });

    if (result === 'cancel') {
      // Usuario canceló - quedarse en el tab actual
      return;
    }

    if (result === 'save') {
      // Usuario quiere guardar primero
      await handleSave();
    }

    // Cambiar al nuevo tab (tanto si descartó como si guardó)
    setActiveTab(newTab);
  }, [hasUnsavedChanges, activeTab, handleSave]);

  /**
   * Opciones de roadClass
   */
  const roadClassOptions = [
    { value: 'motorway', label: 'Autopista' },
    { value: 'trunk', label: 'Troncal' },
    { value: 'primary', label: 'Primaria' },
    { value: 'secondary', label: 'Secundaria' },
    { value: 'tertiary', label: 'Terciaria' },
    { value: 'residential', label: 'Residencial' },
    { value: 'service', label: 'Servicio' },
    { value: 'pedestrian', label: 'Peatonal' },
    { value: 'cycleway', label: 'Ciclovía' },
    { value: 'footway', label: 'Senda' },
    { value: 'unclassified', label: 'No clasificada' },
  ];

  /**
   * Opciones de surfaceType
   */
  const surfaceTypeOptions = [
    { value: 'asphalt', label: 'Asfalto' },
    { value: 'concrete', label: 'Hormigón' },
    { value: 'paved', label: 'Pavimentada' },
    { value: 'cobblestone', label: 'Adoquín' },
    { value: 'gravel', label: 'Grava' },
    { value: 'dirt', label: 'Tierra' },
    { value: 'unpaved', label: 'Sin pavimentar' },
  ];

  /**
   * Opciones de streetForm (B - Morfología Urbana)
   */
  const streetFormOptions = [
    { value: 'street', label: 'Calle' },
    { value: 'avenue', label: 'Avenida' },
    { value: 'boulevard', label: 'Bulevar' },
    { value: 'alley', label: 'Pasaje' },
    { value: 'passage', label: 'Paseo' },
    { value: 'road', label: 'Camino' },
  ];

  /**
   * Opciones de exclusiveMode (C - Modo y Accesibilidad)
   */
  const exclusiveModeOptions = [
    { value: 'none', label: 'Sin restricción' },
    { value: 'pedestrian_only', label: 'Solo peatones' },
    { value: 'bike_only', label: 'Solo bicicletas' },
    { value: 'transit_only', label: 'Solo transporte público' },
  ];

  /**
   * Opciones de accessRestriction (C - Modo y Accesibilidad)
   */
  const accessRestrictionOptions = [
    { value: 'none', label: 'Sin restricción' },
    { value: 'private', label: 'Privado' },
    { value: 'restricted', label: 'Restringido' },
    { value: 'delivery_only', label: 'Solo reparto' },
    { value: 'emergency_only', label: 'Solo emergencias' },
  ];

  /**
   * Opciones de parking (D - Sentido y Configuración)
   */
  const parkingOptions = [
    { value: 'none', label: 'Sin estacionamiento' },
    { value: 'both', label: 'Ambos lados' },
    { value: 'right', label: 'Lado derecho' },
    { value: 'left', label: 'Lado izquierdo' },
  ];

  /**
   * Opciones de condition (E - Superficie y Estado)
   */
  const conditionOptions = [
    { value: 'good', label: 'Bueno' },
    { value: 'fair', label: 'Regular' },
    { value: 'poor', label: 'Malo' },
    { value: 'under_construction', label: 'En construcción' },
    { value: 'unknown', label: 'Desconocido' },
  ];

  /**
   * Opciones de smoothness (E - Superficie y Estado)
   */
  const smoothnessOptions = [
    { value: 'excellent', label: 'Excelente' },
    { value: 'good', label: 'Bueno' },
    { value: 'intermediate', label: 'Intermedio' },
    { value: 'bad', label: 'Malo' },
    { value: 'very_bad', label: 'Muy malo' },
    { value: 'impassable', label: 'Intransitable' },
  ];

  /**
   * Handlers para multi-select de accessMode
   */
  const toggleAccessMode = (mode: AccessMode) => {
    setEditedAccessMode(prev => {
      if (prev.includes(mode)) {
        return prev.filter(m => m !== mode);
      } else {
        return [...prev, mode];
      }
    });
  };

  return (
    <div className="edge-details-panel">
      {/* Toast de éxito */}
      <SuccessToast
        message={successMessage}
        onClose={() => setSuccessMessage(null)}
      />

      {/* Header del panel */}
      <header className="edge-details-panel-header">
        <div className="edge-details-panel-title-group">
          <GitBranch size={18} />
          <h2 className="edge-details-panel-title">{panelTitle}</h2>
        </div>
        <button
          className="edge-details-panel-close-btn"
          onClick={onClose}
          title="Cerrar panel"
          aria-label="Cerrar panel de detalles"
        >
          <X size={16} />
        </button>
      </header>

      {/* Tabs navigation */}
      <nav className="edge-details-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'general'}
          aria-controls="tab-general"
          className={`edge-details-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => handleTabChange('general')}
        >
          <FileText size={14} />
          <span>General</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'caracterizacion'}
          aria-controls="tab-caracterizacion"
          className={`edge-details-tab ${activeTab === 'caracterizacion' ? 'active' : ''}`}
          onClick={() => handleTabChange('caracterizacion')}
        >
          <Layers size={14} />
          <span>Caracterización</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'syntax'}
          aria-controls="tab-syntax"
          className={`edge-details-tab ${activeTab === 'syntax' ? 'active' : ''}`}
          onClick={() => handleTabChange('syntax')}
        >
          <Braces size={14} />
          <span>Sintaxis Espacial</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'structure'}
          aria-controls="tab-structure"
          className={`edge-details-tab ${activeTab === 'structure' ? 'active' : ''}`}
          onClick={() => handleTabChange('structure')}
        >
          <TrendingUp size={14} />
          <span>{DERIVED_ANALYSIS_LABEL}</span>
        </button>
        {populationModeActive && (
          <button
            role="tab"
            aria-selected={activeTab === 'population'}
            aria-controls="tab-population"
            className={`edge-details-tab ${activeTab === 'population' ? 'active' : ''}`}
            onClick={() => handleTabChange('population')}
          >
            <Users size={14} />
            <span>Población</span>
          </button>
        )}
      </nav>

      {/* Contenido de tabs */}
      <div className="edge-details-panel-content">
        {loading && (
          <div className="edge-details-panel-loading">
            <div className="spinner" />
            <p>Cargando detalles...</p>
          </div>
        )}

        {error && (
          <div className="edge-details-panel-error">
            <p>{error}</p>
          </div>
        )}

        {edge && !loading && !error && (
          <>
            {/* Tab: General */}
            {activeTab === 'general' && (
              <div role="tabpanel" id="tab-general" aria-labelledby="tab-general">
                <section className="edge-details-section">
              <h3 className="edge-details-section-title">
                <Route size={14} />
                Identificación semántica
              </h3>

              {semanticLoading && !semantic && (
                <div className="edge-details-note">
                  Cargando identificación semántica del segmento...
                </div>
              )}

              {semanticError && (
                <div className="edge-details-note edge-details-note--warning">
                  {semanticError}
                </div>
              )}

              {!semanticLoading && !semantic && !semanticError && (
                <div className="edge-details-note">
                  <strong>Segmento no codificado.</strong> Todavía no hay una ficha semántica disponible
                  para este tramo.
                </div>
              )}

              {semantic && (
                <>
                  <div className="edge-details-field edge-details-field--highlighted">
                    <span className="edge-details-label">Nombre visible:</span>
                    <span className="edge-details-value">{getEdgeSemanticDisplayName(semantic)}</span>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Código:</span>
                    <span className="edge-details-value edge-details-value-mono">
                      {semantic.semanticCode || 'No determinado'}
                    </span>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Sector:</span>
                    <span className="edge-details-value">{semantic.sectorCode || 'No determinado'}</span>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Estado:</span>
                    <span className={`edge-details-badge edge-details-badge-semantic-${getEdgeNamingStatusTone(semantic.namingStatus)}`}>
                      {getEdgeNamingStatusLabel(semantic.namingStatus)}
                    </span>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Confianza:</span>
                    <span className="edge-details-value">{formatConfidenceScore(semantic.confidenceScore)}</span>
                  </div>
                  {semantic.streetNameResolved && (
                    <div className="edge-details-field">
                      <span className="edge-details-label">Nombre base resuelto:</span>
                      <span className="edge-details-value">{semantic.streetNameResolved}</span>
                    </div>
                  )}
                  {(semanticFromCrossStreet || semanticToCrossStreet) && (
                    <div className="edge-details-field">
                      <span className="edge-details-label">Tramo:</span>
                      <span className="edge-details-value">
                        {semanticFromCrossStreet || 'Sin cruce inicial'}
                        {' → '}
                        {semanticToCrossStreet || 'Sin cruce final'}
                      </span>
                    </div>
                  )}
                  <div className="edge-details-field">
                    <span className="edge-details-label">Tipo de vía:</span>
                    <span className="edge-details-value">{getRoadTypeLabel(semantic.roadType)}</span>
                  </div>
                </>
              )}
            </section>

                {/* ID */}
                <section className="edge-details-section">
              <h3 className="edge-details-section-title">
                <Info size={14} />
                Identificación técnica
              </h3>
              <div className="edge-details-field">
                <span className="edge-details-label">ID:</span>
                <span className="edge-details-value edge-details-value-mono">{edge.id}</span>
              </div>
              <div className="edge-details-field">
                <span className="edge-details-label">Versión:</span>
                <span className="edge-details-value">{edge.edgeVersion}</span>
              </div>
              <div className="edge-details-field">
                <span className="edge-details-label">Estado:</span>
                <span className={`edge-details-badge edge-details-badge-${edge.status}`}>
                  {edge.status === 'active' ? 'Activo' : 'Deprecado'}
                </span>
              </div>
            </section>

            {/* Conexión */}
            <section className="edge-details-section">
              <h3 className="edge-details-section-title">
                <MapPin size={14} />
                Conexión
              </h3>
              <div className="edge-details-field">
                <span className="edge-details-label">Desde nodo:</span>
                <span className="edge-details-value edge-details-value-mono">{edge.fromNode.id.slice(0, 8)}...</span>
              </div>
              <div className="edge-details-field">
                <span className="edge-details-label">Hacia nodo:</span>
                <span className="edge-details-value edge-details-value-mono">{edge.toNode.id.slice(0, 8)}...</span>
              </div>
            </section>

            {/* Geometría */}
            <section className="edge-details-section">
              <h3 className="edge-details-section-title">
                <Navigation size={14} />
                Geometría
              </h3>
              <div className="edge-details-field">
                <span className="edge-details-label">Longitud:</span>
                <span className="edge-details-value">
                  {edge.lengthMeters ? `${(typeof edge.lengthMeters === 'string' ? parseFloat(edge.lengthMeters) : edge.lengthMeters).toFixed(2)} m` : 'N/D'}
                </span>
              </div>
              <div className="edge-details-field">
                <span className="edge-details-label">Puntos:</span>
                <span className="edge-details-value">{edge.geometry.coordinates.length}</span>
              </div>
              {edge.widthMeters && (
                <div className="edge-details-field">
                  <span className="edge-details-label">Ancho:</span>
                  <span className="edge-details-value">{(typeof edge.widthMeters === 'string' ? parseFloat(edge.widthMeters) : edge.widthMeters).toFixed(2)} m</span>
                </div>
              )}
            </section>

            {/* Topología */}
            <section className="edge-details-section">
              <h3 className="edge-details-section-title">
                <Layers size={14} />
                Topología
              </h3>
              <div className="edge-details-field">
                <span className="edge-details-label">Capa:</span>
                <span className="edge-details-value">
                  {edge.layer === 0 ? 'Nivel de suelo' : edge.layer > 0 ? `Puente (+${edge.layer})` : `Túnel (${edge.layer})`}
                </span>
              </div>
              {edge.parentEdgeId && (
                <div className="edge-details-field">
                  <span className="edge-details-label">Edge padre:</span>
                  <span className="edge-details-value edge-details-value-mono">
                    {edge.parentEdgeId.slice(0, 8)}...
                  </span>
                </div>
              )}
            </section>

            {/* Origen */}
            <section className="edge-details-section">
              <h3 className="edge-details-section-title">Origen</h3>
              <div className="edge-details-field">
                <span className="edge-details-label">Fuente:</span>
                <span className={`edge-details-badge edge-details-badge-${edge.sourceType}`}>
                  {edge.sourceType === 'osm' ? 'Red base' :
                   edge.sourceType === 'manual' ? 'Manual' :
                   edge.sourceType === 'municipal' ? 'Municipal' :
                   'Importado'}
                </span>
              </div>
              {edge.sourceReference && (
                <div className="edge-details-field">
                  <span className="edge-details-label">Referencia técnica:</span>
                  <span className="edge-details-value edge-details-value-mono">{edge.sourceReference}</span>
                </div>
              )}
              <div className="edge-details-field">
                <span className="edge-details-label">Autoridad manual:</span>
                <span className="edge-details-value">
                  {edge.isManualAuthority ? 'Sí' : 'No'}
                </span>
              </div>
            </section>

            {/* Metadata */}
            <section className="edge-details-section">
              <h3 className="edge-details-section-title">Metadata</h3>
              <div className="edge-details-field">
                <span className="edge-details-label">Creado:</span>
                <span className="edge-details-value">
                  {new Date(edge.createdAt).toLocaleString('es-AR')}
                </span>
              </div>
              <div className="edge-details-field">
                <span className="edge-details-label">Modificado:</span>
                <span className="edge-details-value">
                  {new Date(edge.updatedAt).toLocaleString('es-AR')}
                </span>
              </div>
            </section>

            {/* Objetos Asociados */}
            {projectId && (
              <AssociatedObjectsSection
                projectId={projectId}
                edgeId={edgeId}
              />
            )}
              </div>
            )}

            {/* Tab: Caracterización */}
            {activeTab === 'caracterizacion' && (
              <div role="tabpanel" id="tab-caracterizacion" aria-labelledby="tab-caracterizacion">
                {/* Sección A: Jerarquía Funcional (OBLIGATORIO) */}
                <section className="edge-details-section">
                  <h3 className="edge-details-section-title">
                    <Route size={14} />
                    A · Jerarquía Funcional
                  </h3>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Clase de vía:</span>
                    <select
                      className="edge-details-select"
                      value={editedRoadClass || ''}
                      onChange={(e) => setEditedRoadClass(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Seleccionar...</option>
                      {roadClassOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </section>

                {/* Sección B: Morfología Urbana */}
                <section className="edge-details-section">
                  <h3 className="edge-details-section-title">
                    <Building2 size={14} />
                    B · Morfología Urbana
                  </h3>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Tipo de vía:</span>
                    <select
                      className="edge-details-select"
                      value={editedStreetForm || ''}
                      onChange={(e) => setEditedStreetForm(e.target.value as StreetForm || null)}
                      disabled={saving}
                    >
                      <option value="">Sin especificar</option>
                      {streetFormOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </section>

                {/* Sección C: Modo y Accesibilidad */}
                <section className="edge-details-section">
                  <h3 className="edge-details-section-title">
                    <Bike size={14} />
                    C · Modo y Accesibilidad
                  </h3>
                  <div className="edge-details-field-vertical">
                    <span className="edge-details-label">Modos permitidos:</span>
                    <div className="edge-details-checkbox-group">
                      <label className="edge-details-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editedAccessMode.includes('vehicle')}
                          onChange={() => toggleAccessMode('vehicle')}
                          disabled={saving}
                        />
                        <span>Vehículo</span>
                      </label>
                      <label className="edge-details-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editedAccessMode.includes('bicycle')}
                          onChange={() => toggleAccessMode('bicycle')}
                          disabled={saving}
                        />
                        <span>Bicicleta</span>
                      </label>
                      <label className="edge-details-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editedAccessMode.includes('pedestrian')}
                          onChange={() => toggleAccessMode('pedestrian')}
                          disabled={saving}
                        />
                        <span>Peatón</span>
                      </label>
                    </div>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Modo exclusivo:</span>
                    <select
                      className="edge-details-select"
                      value={editedExclusiveMode || 'none'}
                      onChange={(e) => setEditedExclusiveMode(e.target.value as ExclusiveMode)}
                      disabled={saving}
                    >
                      {exclusiveModeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Restricción de acceso:</span>
                    <select
                      className="edge-details-select"
                      value={editedAccessRestriction || 'none'}
                      onChange={(e) => setEditedAccessRestriction(e.target.value as AccessRestriction)}
                      disabled={saving}
                    >
                      {accessRestrictionOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </section>

                {/* Sección D: Sentido y Configuración */}
                <section className="edge-details-section">
                  <h3 className="edge-details-section-title">
                    <Gauge size={14} />
                    D · Sentido y Configuración
                  </h3>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Sentido único:</span>
                    <label className="edge-details-toggle-inline">
                      <input
                        type="checkbox"
                        checked={editedOneway}
                        onChange={(e) => {
                          setEditedOneway(e.target.checked);
                          if (!e.target.checked) {
                            setEditedOnewayDirection(null);
                          } else if (!editedOnewayDirection) {
                            setEditedOnewayDirection('forward');
                          }
                        }}
                        disabled={saving}
                      />
                      <span>{editedOneway ? 'Sí' : 'No'}</span>
                    </label>
                  </div>
                  {editedOneway && (
                    <div className="edge-details-field">
                      <span className="edge-details-label">Dirección:</span>
                      <select
                        className="edge-details-select"
                        value={editedOnewayDirection || 'forward'}
                        onChange={(e) => setEditedOnewayDirection(e.target.value as OnewayDirection)}
                        disabled={saving}
                      >
                        <option value="forward">Adelante</option>
                        <option value="backward">Atrás</option>
                      </select>
                    </div>
                  )}
                  <div className="edge-details-field">
                    <span className="edge-details-label">Carriles:</span>
                    <input
                      type="number"
                      className="edge-details-select"
                      min="1"
                      max="12"
                      value={editedLanes || ''}
                      onChange={(e) => setEditedLanes(e.target.value ? parseInt(e.target.value, 10) : null)}
                      placeholder="Sin especificar"
                      disabled={saving}
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Estacionamiento:</span>
                    <select
                      className="edge-details-select"
                      value={editedParking || 'none'}
                      onChange={(e) => setEditedParking(e.target.value as ParkingConfig)}
                      disabled={saving}
                    >
                      {parkingOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </section>

                {/* Sección E: Superficie y Estado */}
                <section className="edge-details-section">
                  <h3 className="edge-details-section-title">
                    <Construction size={14} />
                    E · Superficie y Estado
                  </h3>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Superficie:</span>
                    <select
                      className="edge-details-select"
                      value={editedSurfaceType || ''}
                      onChange={(e) => setEditedSurfaceType(e.target.value as SurfaceType || null)}
                      disabled={saving}
                    >
                      <option value="">Seleccionar...</option>
                      {surfaceTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Estado:</span>
                    <select
                      className="edge-details-select"
                      value={editedCondition || ''}
                      onChange={(e) => setEditedCondition(e.target.value as ConditionType || null)}
                      disabled={saving}
                    >
                      <option value="">Sin especificar</option>
                      {conditionOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Suavidad:</span>
                    <select
                      className="edge-details-select"
                      value={editedSmoothness || ''}
                      onChange={(e) => setEditedSmoothness(e.target.value as SmoothnessType || null)}
                      disabled={saving}
                    >
                      <option value="">Sin especificar</option>
                      {smoothnessOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </section>

                {/* Sección F: Velocidad */}
                <section className="edge-details-section">
                  <h3 className="edge-details-section-title">
                    <Zap size={14} />
                    F · Velocidad
                  </h3>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Límite legal (km/h):</span>
                    <input
                      type="number"
                      className="edge-details-select"
                      min="0"
                      max="200"
                      step="5"
                      value={editedSpeedLimit || ''}
                      onChange={(e) => setEditedSpeedLimit(e.target.value ? parseInt(e.target.value, 10) : null)}
                      placeholder="Sin límite"
                      disabled={saving}
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Velocidad típica (km/h):</span>
                    <input
                      type="number"
                      className="edge-details-select"
                      min="0"
                      max="200"
                      step="5"
                      value={editedTypicalSpeed || ''}
                      onChange={(e) => setEditedTypicalSpeed(e.target.value ? parseInt(e.target.value, 10) : null)}
                      placeholder="No especificada"
                      disabled={saving}
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                  <p className="edge-details-hint">
                    La velocidad típica puede provenir de heurística (por clase + superficie + estado), datos abiertos, o ser editada manualmente.
                  </p>
                </section>

                {/* Sección G: Restricciones Físicas */}
                <section className="edge-details-section">
                  <h3 className="edge-details-section-title">
                    <ShieldAlert size={14} />
                    G · Restricciones Físicas
                  </h3>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Altura máx. (m):</span>
                    <input
                      type="number"
                      className="edge-details-select"
                      min="0"
                      max="10"
                      step="0.1"
                      value={editedMaxHeightM ?? ''}
                      onChange={(e) => setEditedMaxHeightM(e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="Sin límite"
                      disabled={saving}
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Peso máx. (t):</span>
                    <input
                      type="number"
                      className="edge-details-select"
                      min="0"
                      max="100"
                      step="0.5"
                      value={editedMaxWeightT ?? ''}
                      onChange={(e) => setEditedMaxWeightT(e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="Sin límite"
                      disabled={saving}
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                  <div className="edge-details-field">
                    <span className="edge-details-label">Ancho máx. (m):</span>
                    <input
                      type="number"
                      className="edge-details-select"
                      min="0"
                      max="10"
                      step="0.1"
                      value={editedMaxWidthM ?? ''}
                      onChange={(e) => setEditedMaxWidthM(e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="Sin límite"
                      disabled={saving}
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                </section>
              </div>
            )}

            {/* Tab: Sintaxis Espacial */}
            {activeTab === 'syntax' && (
              <div role="tabpanel" id="tab-syntax" aria-labelledby="tab-syntax">
                {hasSyntaxMetrics ? (
                  <>
                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <Braces size={14} />
                        Métrica Activa
                      </h3>
                      <div className="edge-details-highlight-card">
                        <span className="edge-details-highlight-label">
                          {SYNTAX_METRIC_LABELS[selectedMetric]}
                        </span>
                        <span className="edge-details-highlight-value">
                          {formatAnalysisValue(selectedSyntaxMetricValue, 4)}
                        </span>
                        <span className="edge-details-highlight-subtitle">
                          {SYNTAX_METRIC_DESCRIPTIONS[selectedMetric]}
                        </span>
                      </div>
                    </section>

                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <Activity size={14} />
                        Métricas del Segmento
                      </h3>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Integration global:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(syntaxMetric?.integrationGlobal, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Integration global raw:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(syntaxMetric?.integrationGlobalRaw, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Integration r1200:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(syntaxMetric?.integrationR1200, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Integration r800:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(syntaxMetric?.integrationR800, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Choice global:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(syntaxMetric?.choiceGlobal, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Choice r1200:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(syntaxMetric?.choiceR1200, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Choice r800:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(syntaxMetric?.choiceR800, 6)}
                        </span>
                      </div>
                    </section>

                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <Info size={14} />
                        Cómo leerlo
                      </h3>
                      <div className="edge-details-note">
                        <strong>Integration:</strong> {ANALYSIS_HELP_TEXT.integration}
                      </div>
                      <div className="edge-details-note">
                        <strong>Choice:</strong> {ANALYSIS_HELP_TEXT.choice}
                      </div>
                      <div className="edge-details-note">
                        <strong>Global y radios:</strong> {ANALYSIS_HELP_TEXT.global} {ANALYSIS_HELP_TEXT.radius}
                      </div>
                    </section>

                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <Info size={14} />
                        Trazabilidad
                      </h3>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Estado:</span>
                        <span className={`edge-details-badge ${isStale ? 'edge-details-badge-stale' : 'edge-details-badge-valid'}`}>
                          {isStale ? 'Desactualizada' : 'Vigente'}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Contexto:</span>
                        <span className="edge-details-value">
                          {syntaxStatus === 'ready'
                            ? 'Métricas disponibles'
                            : syntaxStatus === 'calculating'
                              ? 'Calculando'
                              : syntaxStatus === 'loading'
                                ? 'Cargando'
                                : syntaxStatus === 'error'
                                  ? 'Error'
                                  : 'Sin cálculo'}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Calculado:</span>
                        <span className="edge-details-value">
                          {formatDateTime(syntaxCalculatedAt)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Sintaxis:</span>
                        <span className="edge-details-value">v{syntaxVersion}</span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Rango visual:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {currentMetricVisualRange
                            ? `${formatAnalysisValue(currentMetricVisualRange.min, 4)} → ${formatAnalysisValue(currentMetricVisualRange.max, 4)}`
                            : 'N/D'}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Basado en red:</span>
                        <span className="edge-details-value">
                          v{syntaxBasedOnNetworkVersion ?? 'N/D'} / actual v{urbanNetworkVersion}
                        </span>
                      </div>
                    </section>
                  </>
                ) : (
                  <div className="edge-details-empty-state">
                    <div className="edge-details-empty-icon">
                      <Braces size={20} />
                    </div>
                    <p className="edge-details-empty-title">Sin métricas de sintaxis para este segmento</p>
                    <p className="edge-details-empty-text">
                      {syntaxStatus === 'calculating' || syntaxStatus === 'loading'
                        ? 'El análisis de sintaxis todavía está en proceso o cargándose.'
                        : 'No hay métricas de sintaxis disponibles para el tramo seleccionado.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Prioridad Estructural */}
            {activeTab === 'structure' && (
              <div role="tabpanel" id="tab-structure" aria-labelledby="tab-structure">
                {hierarchyData ? (
                  <>
                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <TrendingUp size={14} />
                        {DERIVED_ANALYSIS_LABEL}
                      </h3>
                      <div className="edge-details-highlight-card">
                        <span className="edge-details-highlight-label">
                          Perfil activo: {HIERARCHY_PROFILE_LABELS[hierarchyProfile]}
                        </span>
                        <span className="edge-details-highlight-value">
                          Decil {hierarchyData.hierarchyDecile}
                        </span>
                        <span className={`edge-details-inline-badge edge-details-inline-badge--${hierarchyData.hierarchyBand}`}>
                          {getHierarchyBandLabel(hierarchyData.hierarchyBand)}
                        </span>
                        <span className="edge-details-highlight-subtitle">
                          {HIERARCHY_PROFILE_DESCRIPTIONS[hierarchyProfile]}
                        </span>
                      </div>
                    </section>

                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <Activity size={14} />
                        Scores
                      </h3>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Combined score:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(hierarchyData.combinedScore, 4)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Combined percentile:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatPercent(hierarchyData.combinedPercentile)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Global score:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(hierarchyData.globalScore, 4)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Local score:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(hierarchyData.localScore, 4)}
                        </span>
                      </div>
                    </section>

                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <Info size={14} />
                        Cómo leerlo
                      </h3>
                      <div className="edge-details-note">
                        <strong>{DERIVED_ANALYSIS_LABEL}:</strong> {ANALYSIS_HELP_TEXT.structure}
                      </div>
                      <div className="edge-details-note">
                        <strong>Perfil {HIERARCHY_PROFILE_LABELS[hierarchyProfile]}:</strong> {HIERARCHY_PROFILE_DESCRIPTIONS[hierarchyProfile]}
                      </div>
                    </section>

                    <section className="edge-details-section">
                      <h3 className="edge-details-section-title">
                        <Info size={14} />
                        Auditoría
                      </h3>
                      <div className="edge-details-field">
                        <span className="edge-details-label">NAIN Global raw:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(hierarchyData.rawNainGlobal, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">NAIN Local raw:</span>
                        <span className="edge-details-value edge-details-value-mono">
                          {formatAnalysisValue(hierarchyData.rawNainLocal, 6)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Longitud:</span>
                        <span className="edge-details-value">
                          {formatLengthMeters(hierarchyData.lengthMeters)}
                        </span>
                      </div>
                      <div className="edge-details-field">
                        <span className="edge-details-label">Microsegmento:</span>
                        <span className="edge-details-value">
                          {hierarchyData.isShortSegment ? 'Sí (< 20m)' : 'No'}
                        </span>
                      </div>
                    </section>

                    {hierarchyData.isShortSegment && (
                      <div className="edge-details-note edge-details-note--warning">
                        Segmento corto detectado. La prioridad estructural puede ser más sensible a artefactos topológicos locales.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="edge-details-empty-state">
                    <div className="edge-details-empty-icon">
                      <TrendingUp size={20} />
                    </div>
                    <p className="edge-details-empty-title">Sin datos de {DERIVED_ANALYSIS_LABEL.toLowerCase()} para este segmento</p>
                    <p className="edge-details-empty-text">
                      No hay lectura derivada disponible para el tramo seleccionado con el perfil actual.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── TAB: POBLACIÓN ─────────────────────────────────────────── */}
            {activeTab === 'population' && (
              <div role="tabpanel" id="tab-population" aria-labelledby="tab-population">
                {/* Estados sin datos */}
                {populationRunStatus === 'no_runs' && (
                  <div className="edge-details-empty">
                    <p className="edge-details-empty-text">
                      No hay datos de población calculados para este proyecto. Abrí el panel de Población y ejecutá el cálculo.
                    </p>
                  </div>
                )}
                {(populationRunStatus === 'running' || populationRunStatus === 'pending') && (
                  <div className="edge-details-empty">
                    <p className="edge-details-empty-text">Cálculo en curso…</p>
                  </div>
                )}
                {populationRunStatus === 'failed' && (
                  <div className="edge-details-empty">
                    <p className="edge-details-empty-text">El cálculo de población falló. Revisá el panel de Población.</p>
                  </div>
                )}

                {/* Run completado: mostrar detalle */}
                {populationRunStatus === 'completed' && (() => {
                  const VAR_LABELS: Record<PopulationVariable, string> = {
                    population_per_km:     'Población / km',
                    population:            'Población asignada',
                    households_per_km:     'Hogares / km',
                    households:            'Hogares asignados',
                    nbi_households_per_km: 'Hogares NBI / km',
                    nbi_households:        'Hogares NBI asignados',
                  };
                  const VAR_UNIT: Record<PopulationVariable, string> = {
                    population_per_km:     'hab/km',
                    population:            'hab.',
                    households_per_km:     'hog./km',
                    households:            'hog.',
                    nbi_households_per_km: 'NBI/km',
                    nbi_households:        'hog. NBI',
                  };
                  const fmtN = (v: number | null, unit?: string): string => {
                    if (v === null) return 'N/D';
                    const num = v >= 1000 ? `${(v / 1000).toFixed(1)}k`
                              : v >= 100  ? v.toFixed(0)
                              : v >= 1    ? v.toFixed(1)
                              : v > 0     ? v.toFixed(2) : '0';
                    return unit ? `${num} ${unit}` : num;
                  };

                  const activeVal = getPopValue(selectedPopVariable);
                  const pRank     = populationPercentileRank;
                  const ramp      = COLOR_RAMPS[DEFAULT_SYNTAX_RAMP];
                  const gradientStops = ramp.stops.map((s) => `${s.color} ${s.stop * 100}%`).join(', ');
                  const ALL_VARS: PopulationVariable[] = [
                    'population_per_km', 'population',
                    'households_per_km', 'households',
                    'nbi_households_per_km', 'nbi_households',
                  ];

                  // Use detail data when available, fallback to GeoJSON props
                  const d = populationDetail;
                  const isRefinedMethod = selectedPopMethod === 'building_refined';
                  const lenM    = d?.networkLengthM ?? populationEdgeProps?.network_length_m ?? null;
                  const lenKm   = lenM != null ? (lenM / 1000).toFixed(3) : null;
                  const srcCount = d?.sourceRadiosCount ?? populationEdgeProps?.source_radios_count ?? null;
                  const srcBuildingsCount = d?.sourceBuildingsCount ?? populationEdgeProps?.source_buildings_count ?? null;
                  const hasNoData = d?.reason === 'no_population_assigned' || (!populationEdgeProps && !d);

                  return (
                    <>
                      {/* Loading */}
                      {populationDetailLoading && (
                        <div style={{ padding: '12px', fontSize: 12, color: '#888', display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div className="spinner-small" />
                          Cargando detalle…
                        </div>
                      )}

                      {/* Error */}
                      {populationDetailError && (
                        <div style={{ padding: '8px 12px', fontSize: 11, color: '#f87171', background: 'rgba(248,113,113,0.08)', borderRadius: 4, margin: '8px 0' }}>
                          {populationDetailError}
                        </div>
                      )}

                      {/* Segmento sin población */}
                      {hasNoData && !populationDetailLoading && (
                        <div className="edge-details-empty">
                          <p className="edge-details-empty-text">
                            Este segmento no tiene población asignada. Puede ser un tramo muy corto o ubicado fuera de los radios censales del proyecto.
                          </p>
                        </div>
                      )}

                      {/* Datos disponibles */}
                      {!hasNoData && (populationEdgeProps || d) && (
                        <>
                          {/* Variable activa — highlight con percentil */}
                          <section className="edge-details-section">
                            <h3 className="edge-details-section-title">
                              <Users size={14} />
                              {isRefinedMethod ? 'Población — Refinado edilicio' : 'Población por segmento'}
                            </h3>
                            <div className="edge-details-highlight-card">
                              <span className="edge-details-highlight-label">
                                {VAR_LABELS[selectedPopVariable]}
                              </span>
                              <span className="edge-details-highlight-value">
                                {fmtN(activeVal, VAR_UNIT[selectedPopVariable])}
                              </span>
                              {pRank !== null && (
                                <>
                                  <div style={{ height: 8, borderRadius: 4, background: `linear-gradient(to right, ${gradientStops})`, margin: '8px 0 2px', position: 'relative' }}>
                                    <div style={{
                                      position: 'absolute',
                                      left: `${pRank * 100}%`,
                                      top: -3,
                                      width: 2,
                                      height: 14,
                                      background: '#fff',
                                      borderRadius: 1,
                                      transform: 'translateX(-50%)',
                                    }} />
                                  </div>
                                  <span className="edge-details-highlight-subtitle">
                                    Percentil {Math.round(pRank * 100)} en el proyecto (p95)
                                  </span>
                                </>
                              )}
                            </div>
                          </section>

                          {/* Tabla de todas las variables */}
                          <section className="edge-details-section">
                            <h3 className="edge-details-section-title">
                              <Activity size={14} />
                              Detalle del segmento
                            </h3>
                            {/* Si tenemos detail del API, mostramos sus valores */}
                            {d && !d.reason ? (
                              <>
                                <div className={`edge-details-field${selectedPopVariable === 'population_per_km'   ? ' edge-details-field--highlighted' : ''}`}>
                                  <span className="edge-details-label">Población estimada:</span>
                                  <span className="edge-details-value edge-details-value-mono">{fmtN(d.populationEstimated, 'hab.')}</span>
                                </div>
                                <div className={`edge-details-field${selectedPopVariable === 'population_per_km' || selectedPopVariable === 'population' ? ' edge-details-field--highlighted' : ''}`}
                                     style={selectedPopVariable === 'population_per_km' ? {} : {}}>
                                  <span className="edge-details-label">Población / km:</span>
                                  <span className="edge-details-value edge-details-value-mono">{fmtN(d.populationPerKm, 'hab/km')}</span>
                                </div>
                                <div className={`edge-details-field${selectedPopVariable === 'households' ? ' edge-details-field--highlighted' : ''}`}>
                                  <span className="edge-details-label">Hogares estimados:</span>
                                  <span className="edge-details-value edge-details-value-mono">{fmtN(d.householdsEstimated, 'hog.')}</span>
                                </div>
                                <div className={`edge-details-field${selectedPopVariable === 'households_per_km' ? ' edge-details-field--highlighted' : ''}`}>
                                  <span className="edge-details-label">Hogares / km:</span>
                                  <span className="edge-details-value edge-details-value-mono">{fmtN(d.householdsPerKm, 'hog./km')}</span>
                                </div>
                                <div className={`edge-details-field${selectedPopVariable === 'nbi_households' ? ' edge-details-field--highlighted' : ''}`}>
                                  <span className="edge-details-label">Hogares NBI:</span>
                                  <span className="edge-details-value edge-details-value-mono">{fmtN(d.nbiHouseholdsEstimated, 'hog.')}</span>
                                </div>
                                <div className={`edge-details-field${selectedPopVariable === 'nbi_households_per_km' ? ' edge-details-field--highlighted' : ''}`}>
                                  <span className="edge-details-label">Hogares NBI / km:</span>
                                  <span className="edge-details-value edge-details-value-mono">{fmtN(d.nbiHouseholdsPerKm, 'NBI/km')}</span>
                                </div>
                              </>
                            ) : (
                              /* Fallback: datos del GeoJSON */
                              ALL_VARS.map((v) => (
                                <div key={v} className={`edge-details-field${v === selectedPopVariable ? ' edge-details-field--highlighted' : ''}`}>
                                  <span className="edge-details-label">{VAR_LABELS[v]}:</span>
                                  <span className="edge-details-value edge-details-value-mono">
                                    {fmtN(getPopValue(v), VAR_UNIT[v])}
                                  </span>
                                </div>
                              ))
                            )}

                            {/* Longitud y fuente */}
                            <div className="edge-details-field">
                              <span className="edge-details-label">Longitud red:</span>
                              <span className="edge-details-value edge-details-value-mono">
                                {lenKm !== null ? `${lenKm} km` : 'N/D'}
                              </span>
                            </div>
                            {isRefinedMethod ? (
                              <div className="edge-details-field">
                                <span className="edge-details-label">Edificios aportantes:</span>
                                <span className="edge-details-value edge-details-value-mono">
                                  {srcBuildingsCount ?? 'N/D'}
                                </span>
                              </div>
                            ) : (
                              <div className="edge-details-field">
                                <span className="edge-details-label">Radios de origen:</span>
                                <span className="edge-details-value edge-details-value-mono">
                                  {srcCount ?? 'N/D'}
                                </span>
                              </div>
                            )}

                            {/* Nota metodológica */}
                            <p style={{ fontSize: 10, color: '#666', lineHeight: 1.5, fontStyle: 'italic', marginTop: 10 }}>
                              {isRefinedMethod
                                ? 'Distribuye población censal hacia edificios elegibles y luego a sus segmentos asociados.'
                                : 'Estimación basada en radios censales 2022 redistribuidos sobre la red urbana según longitud de segmento dentro de cada radio.'}
                            </p>
                          </section>

                          {/* Aportes censales — colapsable (census_network_length) */}
                          {!isRefinedMethod && d && (d.contributions ?? []).length > 0 && (
                            <section className="edge-details-section">
                              <button
                                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none', border: 'none', padding: '0 0 8px', cursor: 'pointer', color: 'inherit' }}
                                onClick={() => setContributionsExpanded((v) => !v)}
                              >
                                <h3 className="edge-details-section-title" style={{ margin: 0, flex: 1 }}>
                                  <MapPin size={14} />
                                  Aportes censales
                                </h3>
                                <ChevronDown size={14} style={{ color: '#888', transform: contributionsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
                              </button>
                              {contributionsExpanded && (d.contributions ?? []).map((c) => (
                                <div key={c.radioCode} style={{ padding: '6px 8px', marginBottom: 4, background: 'rgba(255,255,255,0.04)', borderRadius: 4, fontSize: 11 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ color: '#2FA4B5', fontFamily: 'monospace', fontSize: 10 }}>Radio {c.radioCode}</span>
                                    <span style={{ color: '#aaa', fontFamily: 'monospace', fontSize: 10 }}>{c.contributionSharePct.toFixed(1)}%</span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    <span style={{ color: '#ccc' }}>{fmtN(c.populationContribution, 'hab.')}</span>
                                    <span style={{ color: '#999' }}>{fmtN(c.householdsContribution, 'hog.')}</span>
                                    <span style={{ color: '#999' }}>{(c.segmentLengthInsideRadioM).toFixed(0)} m dentro</span>
                                  </div>
                                </div>
                              ))}
                            </section>
                          )}

                          {/* Aportes edilicios — colapsable (building_refined) */}
                          {isRefinedMethod && d && (d.buildingContributions ?? []).length > 0 && (
                            <section className="edge-details-section">
                              <button
                                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none', border: 'none', padding: '0 0 8px', cursor: 'pointer', color: 'inherit' }}
                                onClick={() => setContributionsExpanded((v) => !v)}
                              >
                                <h3 className="edge-details-section-title" style={{ margin: 0, flex: 1 }}>
                                  <MapPin size={14} />
                                  Edificios aportantes (top 25)
                                </h3>
                                <ChevronDown size={14} style={{ color: '#888', transform: contributionsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
                              </button>
                              {contributionsExpanded && (d.buildingContributions ?? []).map((b) => (
                                <div key={b.buildingCentroidId} style={{ padding: '6px 8px', marginBottom: 4, background: 'rgba(255,255,255,0.04)', borderRadius: 4, fontSize: 11 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ color: '#2FA4B5', fontFamily: 'monospace', fontSize: 10 }}>
                                      {b.displayCode ?? b.buildingCentroidId.slice(0, 8)}
                                    </span>
                                    <span style={{ color: '#888', fontSize: 10 }}>Radio {b.radioCode}</span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    <span style={{ color: '#ccc' }}>{fmtN(b.populationContribution, 'hab.')}</span>
                                    <span style={{ color: '#999' }}>{fmtN(b.householdsContribution, 'hog.')}</span>
                                    {b.residentialWeightM2 != null && (
                                      <span style={{ color: '#666', fontSize: 10 }}>
                                        {b.residentialWeightM2.toFixed(0)} m² {b.weightSource === 'footprint_fallback' ? '(huella)' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </section>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Botón de guardar (fijo al fondo) */}
            {hasUnsavedChanges && (
              <div className="edge-details-save-bar">
                <button
                  className="edge-details-save-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="spinner-small" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Guardar cambios
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
